/**
 * Stock display polling for Cardvalley PDP.
 *
 * Why this exists:
 *   Shopify's Fastly CDN caches PDP HTML for 1-5 minutes, which means the
 *   server-rendered {{ variant.inventory_quantity }} value can be stale for
 *   several minutes after a sale. This script bypasses the HTML cache by
 *   re-fetching the count from two live sources, every 10 seconds.
 *
 * How it works:
 *   1. On page load, each `.stock-display` element fetches fresh data from
 *      `/products/{handle}.js` (Shopify's own JSON endpoint, not HTML-cached).
 *   2. Then it polls our Supabase-backed endpoint at cardvalley-dashboard
 *      every 10 seconds for the latest mirrored Shopify stock value.
 *   3. The element's HTML is rebuilt using the same SVG + label markup that
 *      Dawn's Liquid inventory block produces, so visual style stays consistent.
 *
 * Required data attributes on each `.stock-display` element:
 *   - data-product-id           Shopify numeric product id (e.g. 8125...)
 *   - data-product-handle       Shopify product handle for /products/{handle}.js
 *   - data-variant-id           Current variant's numeric id
 *   - data-threshold            Low-stock threshold (integer)
 *   - data-show-count           "true" or "false" — show explicit number?
 *   - data-text-instock-count   Translated template for in-stock + count, with __QTY__ placeholder
 *   - data-text-instock         Translated text for in-stock (no count)
 *   - data-text-lowstock-count  Translated template for low-stock + count
 *   - data-text-lowstock        Translated text for low-stock (no count)
 *   - data-text-soldout         Translated text for sold-out
 */
(function () {
  'use strict';

  var STOCK_ENDPOINT_BASE = 'https://cardvalley-dashboard.vercel.app/api/public/stock/';
  var POLL_INTERVAL_MS = 10000;

  // Dawn-matching SVG circles for in-stock / low-stock / sold-out.
  var SVG_IN_STOCK =
    '<svg width="15" height="15" aria-hidden="true">' +
      '<circle cx="7.5" cy="7.5" r="7.5" fill="rgba(62,214,96,0.3)"></circle>' +
      '<circle cx="7.5" cy="7.5" r="5" stroke="rgb(255,255,255)" stroke-width="1" fill="rgb(62,214,96)"></circle>' +
    '</svg>';
  var SVG_LOW_STOCK =
    '<svg width="15" height="15" aria-hidden="true">' +
      '<circle cx="7.5" cy="7.5" r="7.5" fill="rgba(238,148,65,0.3)"></circle>' +
      '<circle cx="7.5" cy="7.5" r="5" stroke="rgb(255,255,255)" stroke-width="1" fill="rgb(238,148,65)"></circle>' +
    '</svg>';
  var SVG_SOLD_OUT =
    '<svg width="15" height="15" aria-hidden="true">' +
      '<circle cx="7.5" cy="7.5" r="7.5" fill="rgba(200,200,200,0.3)"></circle>' +
      '<circle cx="7.5" cy="7.5" r="5" stroke="rgb(255,255,255)" stroke-width="1" fill="rgb(200,200,200)"></circle>' +
    '</svg>';

  function render(el, qty) {
    var threshold = parseInt(el.dataset.threshold || '0', 10);
    var showCount = el.dataset.showCount === 'true';
    var html;

    if (qty > 0) {
      if (qty <= threshold) {
        var label = showCount
          ? (el.dataset.textLowstockCount || '__QTY__').replace('__QTY__', qty)
          : (el.dataset.textLowstock || '');
        html = SVG_LOW_STOCK + ' ' + label;
      } else {
        var label2 = showCount
          ? (el.dataset.textInstockCount || '__QTY__').replace('__QTY__', qty)
          : (el.dataset.textInstock || '');
        html = SVG_IN_STOCK + ' ' + label2;
      }
    } else {
      html = SVG_SOLD_OUT + ' ' + (el.dataset.textSoldout || '');
    }

    el.innerHTML = html;
    el.classList.toggle('stock-display--out', qty <= 0);
  }

  // 1. Fetch live stock from Shopify's product JSON (bypasses HTML cache).
  //    Defense-in-depth: works even if our endpoint is down.
  async function shopifyFetch(el) {
    try {
      var handle = el.dataset.productHandle;
      if (!handle) return;
      var res = await fetch('/products/' + handle + '.js?_=' + Date.now(), { cache: 'no-store' });
      if (!res.ok) return;
      var product = await res.json();
      var variantId = String(el.dataset.variantId || '');
      var variant = (product.variants || []).find(function (v) { return String(v.id) === variantId; });
      if (variant && typeof variant.inventory_quantity === 'number') {
        render(el, variant.inventory_quantity);
      }
    } catch (e) {
      // Silently fall back to the SSR Liquid value.
    }
  }

  // 2. Poll our Supabase-mirrored stock endpoint every 10 s.
  //    If our endpoint isn't reachable (dev / test store, deploy not live),
  //    fall back to Shopify's own /products/{handle}.js polling so the
  //    storefront still shows up-to-date numbers — just with slightly worse
  //    latency (Shopify updates its JSON ~5-15s after a sale).
  async function pollEndpoint(el) {
    try {
      var productId = el.dataset.productId;
      if (productId) {
        var res = await fetch(STOCK_ENDPOINT_BASE + productId, { cache: 'no-store' });
        if (res.ok) {
          var data = await res.json();
          if (typeof data.shopify_stock === 'number') {
            render(el, data.shopify_stock);
            return;
          }
        }
      }
    } catch (e) {
      // ignore and fall through to Shopify fallback
    }
    // Fallback: re-fetch Shopify's product JSON. Works in any storefront,
    // even where the Cardvalley endpoint isn't deployed or doesn't know
    // about this product.
    shopifyFetch(el);
  }

  function init() {
    var elements = document.querySelectorAll('.stock-display');
    if (!elements.length) return;

    elements.forEach(function (el) {
      // Initial bypass-cache fetch from Shopify, then live polling from our DB.
      shopifyFetch(el);
      pollEndpoint(el);
      setInterval(function () { pollEndpoint(el); }, POLL_INTERVAL_MS);
    });

    // Variant switcher support: when Dawn updates the variant, the page
    // dispatches `variant:change` on the product-info element. Re-read the
    // selected variant id from the form input and re-fetch.
    document.querySelectorAll('product-info').forEach(function (info) {
      info.addEventListener('change', function (e) {
        var variantInput = info.querySelector('input[name="id"]');
        var newVariantId = variantInput && variantInput.value;
        if (!newVariantId) return;
        info.querySelectorAll('.stock-display').forEach(function (el) {
          el.dataset.variantId = newVariantId;
          shopifyFetch(el);
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
